import type { ColumnFilterEntry } from "../../types/claims";
import type { FilterOperator } from "../../types/table";
import type {
  UsageActivityFeed,
  UsageActivityFeedColumnFilters,
} from "../../types/observability";
import { formatAgentLabel } from "./observabilityHelper";

export const formatUsageActivityFeedBoolean = (
  value: boolean | undefined,
): string => (value === true ? "Yes" : value === false ? "No" : "");

export const usageActivityFeedBooleanFilterValue = (
  value: boolean | undefined,
): string => (value === true ? "true" : value === false ? "false" : "");

export const getUsageActivityFeedColumnDisplayValue = (
  row: UsageActivityFeed,
  columnId: string,
): string => {
  switch (columnId) {
    case "classification":
      return row.classification ? formatAgentLabel(row.classification) : "";
    case "has_837":
    case "successful_run":
      return usageActivityFeedBooleanFilterValue(
        row[columnId as keyof UsageActivityFeed] as boolean | undefined,
      );
    case "started_at":
    case "finished_at":
      return row[columnId] ? row[columnId].slice(0, 10) : "";
    case "count_835":
    case "count_document_uploads":
    case "billed_amount":
    case "paid_amount":
    case "cost":
      return row[columnId] != null ? String(row[columnId]) : "";
    default:
      return String(row[columnId as keyof UsageActivityFeed] ?? "").trim();
  }
};

const getFilterEntry = (
  filters: UsageActivityFeedColumnFilters | null,
  columnId: string,
): ColumnFilterEntry | string[] | undefined => {
  if (!filters) return undefined;
  return filters[columnId];
};

export const getUsageActivityFeedColumnFilterValues = (
  filters: UsageActivityFeedColumnFilters | null,
  columnId: string,
): string[] => {
  const raw = getFilterEntry(filters, columnId);
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return (raw.values ?? []).map(String).filter(Boolean);
};

export const getUsageActivityFeedColumnFilterOperator = (
  filters: UsageActivityFeedColumnFilters | null,
  columnId: string,
): FilterOperator => {
  const raw = getFilterEntry(filters, columnId);
  if (!raw || Array.isArray(raw) || !("operator" in raw)) return "and";
  const op = raw.operator;
  return op === "or" || op === "isnot" ? op : "and";
};

const mapFilterValueForDisplay = (columnId: string, value: string): string =>
  columnId === "classification" ? formatAgentLabel(value) : value;

export const getUniqueUsageActivityFeedColumnValues = (
  rows: UsageActivityFeed[] | null,
  columnId: string,
  filters: UsageActivityFeedColumnFilters | null,
): string[] => {
  const fromFilter = getUsageActivityFeedColumnFilterValues(filters, columnId).map(
    (value) => mapFilterValueForDisplay(columnId, value),
  );
  const source =
    fromFilter.length > 0
      ? fromFilter
      : (rows?.map((row) => getUsageActivityFeedColumnDisplayValue(row, columnId)) ??
        []);

  return [...new Set(source.filter((value) => value !== ""))].sort((a, b) =>
    a.localeCompare(b),
  );
};
